<template>
  <v-dialog
    v-model="internalValue"
    class="egov-timeout-overlay d-flex align-center justify-center"
    persistent
    scrollable
    :aria-labelledby="titleId"
    :aria-describedby="titleId"
  >
    <v-card class="egov-timeout-modal" elevation="0" :style="[{ backgroundColor: modalBg }, cardStyle]" ref="dialogRef">
      <EgovIconButton
        v-if="showCloseButton"
        :icon="closeButtonIcon"
        ariaLabel="Close"
        variant="text"
        class="close-btn"
        size="small"
        :style="{ color: iconColor }"
        @click="handleClose"
      />

      <div class="icon-row" aria-hidden="true">
        <v-icon size="30" class="modal-svg-icon" :style="{ color: iconColor }" :icon="icon" />
      </div>

      <header class="egov-timeout-modal__header">
        <h2 class="modal-title" :id="titleId" :style="{ color: textColor }">{{ title }}</h2>
      </header>

      <section class="egov-timeout-modal__body">
        <p v-if="description" class="timeout-description">{{ description }}</p>

        <slot name="body" />

        <div v-if="showTimer" class="countdown-block">
          <h2 class="countdown-label" :style="{ color: textColor }">{{ timerHeading }}</h2>
          <h2 class="countdown-timer" :style="{ color: textColor }">{{ formattedTime }}</h2>
        </div>
      </section>

      <footer v-if="showPrimaryButton || showSecondaryButton" class="egov-timeout-modal__footer">
        <EgovButton
          v-if="showSecondaryButton"
          :label="secondaryButtonLabel"
          variant="text"
          class="footer-btn"
          @click="handleLeftCta"
        />
        <EgovButton
          v-if="showPrimaryButton"
          :label="primaryButtonLabel"
          variant="filled"
          class="footer-btn"
          @click="handleRightCta"
        />
      </footer>
    </v-card>
  </v-dialog>
</template>

<script setup>
import { ref, watch, computed, nextTick } from 'vue'
import { v4 as uuidv4 } from 'uuid'
import EgovIconButton from './EgovIconButton.vue'
import EgovButton from './EgovButton.vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  secondsRemaining: { type: Number, default: 300 },

  icon: { type: String, default: 'ri-hourglass-fill' },
  defaultWidth: { type: String, default: '360px' },
  fixedWidth: { type: Boolean, default: false },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  timerHeading: { type: String, default: 'Time Remaining:' },

  showTimer: { type: Boolean, default: true },

  showCloseButton: { type: Boolean, default: true },
  closeButtonIcon: { type: String, default: 'ri-close-line' },

  showPrimaryButton: { type: Boolean, default: true },
  primaryButtonLabel: { type: String, default: 'Continue Session' },

  showSecondaryButton: { type: Boolean, default: true },
  secondaryButtonLabel: { type: String, default: 'Log Out Now' },
})

const emit = defineEmits(['update:modelValue', 'close', 'left-cta', 'right-cta', 'timeout'])

const cardStyle = computed(() => {
  if (!props.fixedWidth) return { '--modal-min-width': props.defaultWidth }
  return { '--modal-min-width': props.defaultWidth, '--modal-max-width': props.defaultWidth }
})

const internalValue = ref(props.modelValue)
watch(
  () => props.modelValue,
  (v) => (internalValue.value = v),
)
watch(internalValue, (v) => emit('update:modelValue', v))

const titleId = `timeout-modal-title-${uuidv4()}`
const dialogRef = ref(null)

const focusableSelectors = 'button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'

watch(internalValue, async (open) => {
  if (!open) return
  await nextTick()
  const dialog = dialogRef.value?.$el ?? dialogRef.value
  dialog?.querySelectorAll(focusableSelectors)?.[0]?.focus()
})

const timeLeft = ref(props.secondsRemaining)
let interval = null

watch(internalValue, (open) => {
  if (open) {
    timeLeft.value = props.secondsRemaining
    interval = setInterval(() => {
      timeLeft.value--
      if (timeLeft.value <= 0) {
        clearInterval(interval)
        internalValue.value = false
        emit('timeout')
      }
    }, 1000)
  } else if (interval) {
    clearInterval(interval)
  }
})

watch(
  () => props.secondsRemaining,
  (v) => {
    timeLeft.value = v
  },
)

const formattedTime = computed(() => {
  const t = Math.max(0, timeLeft.value)
  const h = Math.floor(t / 3600)
    .toString()
    .padStart(2, '0')
  const m = Math.floor((t % 3600) / 60)
    .toString()
    .padStart(2, '0')
  const s = (t % 60).toString().padStart(2, '0')
  return `${h}:${m}:${s}`
})

const handleClose = () => {
  internalValue.value = false
  emit('close')
}

const handleLeftCta = () => {
  clearInterval(interval)
  internalValue.value = false
  emit('left-cta')
}

const handleRightCta = () => {
  clearInterval(interval)
  internalValue.value = false
  emit('right-cta')
}

const modalBg = 'rgba(223, 232, 255, 1)'
const textColor = 'var(--kds-on-surface)'
const iconColor = 'rgba(82, 96, 112, 1)'
</script>

<style scoped lang="scss">
.egov-timeout-modal {
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  border-radius: 12px !important;
  overflow: hidden;
  padding: 4px;
  width: 100%;
  margin: 0 auto;
  box-shadow: none !important;

  @media (min-width: 600px) {
    width: fit-content;
    min-width: var(--modal-min-width, 360px);
    max-width: var(--modal-max-width, 560px);
  }

  @media (max-width: 599px) {
    max-height: 95vh;
    max-width: calc(100% - 16px);
    border-radius: 8px !important;
  }
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  z-index: 1;

  @media (max-width: 599px) {
    top: 12px;
    right: 12px;
  }
}

.icon-row {
  display: flex;
  justify-content: center;
  padding-top: 20px;

  @media (max-width: 599px) {
    padding-top: 16px;
  }
}

.egov-timeout-modal__header {
  display: flex;
  justify-content: center;
  padding: 20px 40px 12px;

  @media (max-width: 599px) {
    padding: 16px 20px 8px;
  }
}

.modal-title {
  font-size: 24px;
  font-weight: bold;
  margin-top: -5px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal;

  @media (max-width: 599px) {
    font-size: 20px;
  }
}

.egov-timeout-modal__body {
  padding: 0 30px 24px;
  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: 599px) {
    padding: 0 20px 16px;
  }
}

.timeout-description {
  text-align: left;
  margin-bottom: 20px;
  font-size: 14px;
  line-height: 1.5;
}

.countdown-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.countdown-label {
  font-size: 20px;
  font-weight: bold;
  text-align: center;

  @media (max-width: 599px) {
    font-size: 18px;
  }
}

.countdown-timer {
  font-size: 28px;
  font-weight: bold;
  text-align: center;
  font-variant-numeric: tabular-nums;
  letter-spacing: 2px;

  @media (max-width: 599px) {
    font-size: 22px;
  }
}

.egov-timeout-modal__footer {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: center;
  gap: 10px;
  padding: 8px 30px 24px;

  @media (max-width: 599px) {
    flex-wrap: wrap;
    padding: 8px 20px 16px;
  }
}

.footer-btn {
  width: fit-content;

  @media (max-width: 599px) {
    width: 100%;
  }
}
</style>
